import React, { useState } from 'react';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Button, Grid, CircularProgress, Alert, Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../../services/api';
import { format } from 'date-fns';

// Use a default color scheme if styleGuide is not available
const colors = {
  tableHeaderBackground: '#f5f5f5',
  lightBackground: '#f9f9f9',
  success: '#4caf50',
  error: '#f44336'
};

const API_URL = API_BASE_URL;

const ProfitLoss = () => {
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), 0, 1)); // Jan 1 of current year
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  
  const generateReport = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      };
      
      const response = await axios.get(`${API_URL}/accounting/profit-loss`, {
        params,
        headers: getAuthHeaders()
      });
      setReport(response.data);
    } catch (err) {
      console.error('Error generating P&L report:', err);
      setError('Failed to generate Profit & Loss statement. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'LKR' 
    }).format(amount);
  };
  
  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Profit & Loss Statement
      </Typography>
      
      <Paper sx={{ p: 2, mb: 3 }} elevation={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true }
                }}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={5} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: { fullWidth: true }
                }}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={2} md={4}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={generateReport}
              disabled={loading || !startDate || !endDate}
              fullWidth
            >
              Generate Report
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : report ? (
        <Paper sx={{ p: 3 }} elevation={3}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Profit & Loss Statement
            </Typography>
            <Typography variant="subtitle1">
              For the period {format(new Date(report.period.startDate), 'MMMM d, yyyy')} to {format(new Date(report.period.endDate), 'MMMM d, yyyy')}
            </Typography>
          </Box>
          
          {/* Revenue Section */}
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
            Revenue
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: colors.tableHeaderBackground }}>
                  <TableCell>Account</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.revenue.accounts.length > 0 ? (
                  report.revenue.accounts.map((account) => (
                    <TableRow key={account.id} hover>
                      <TableCell>{account.account_code} - {account.name}</TableCell>
                      <TableCell align="right">{formatCurrency(account.balance)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      No revenue accounts found
                    </TableCell>
                  </TableRow>
                )}
                <TableRow sx={{ backgroundColor: colors.lightBackground }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total Revenue</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(report.revenue.total)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Expenses Section */}
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
            Expenses
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: colors.tableHeaderBackground }}>
                  <TableCell>Account</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.expenses.accounts.length > 0 ? (
                  report.expenses.accounts.map((account) => (
                    <TableRow key={account.id} hover>
                      <TableCell>{account.account_code} - {account.name}</TableCell>
                      <TableCell align="right">{formatCurrency(account.balance)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      No expense accounts found
                    </TableCell>
                  </TableRow>
                )}
                <TableRow sx={{ backgroundColor: colors.lightBackground }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total Expenses</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(report.expenses.total)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Net Profit/Loss */}
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, backgroundColor: report.netProfit >= 0 ? colors.success : colors.error, borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
              Net {report.netProfit >= 0 ? 'Profit' : 'Loss'}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
              {formatCurrency(report.netProfit)}
            </Typography>
          </Box>
        </Paper>
      ) : null}
    </Box>
  );
};

export default ProfitLoss;
